import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';

import { Column, GridApi, SortDirection } from 'ag-grid-community';

import { FontIconComponent } from '@camelot/icons';
import { TranslatePipe } from '@camelot/translation';
import { TextComponent } from '@camelot/ui';

import { BaseFilterComponent } from '../../../models/columns/base-col';
import { GridContext } from '../../../models/grid-data';
import { FilterModelSource } from '../../../models/types';

type ColumnFilter = BaseFilterComponent<any, any>;

@Component({
  imports: [FontIconComponent, TextComponent, TranslatePipe],
  selector: 'cam-grid-table-column-menu',
  styleUrl: './grid-table-column-menu.component.scss',
  templateUrl: './grid-table-column-menu.component.html',
})
export class GridTableColumnMenuComponent<T> implements OnInit, AfterViewInit, OnDestroy {
  api = input.required<GridApi<T>>();
  column = input.required<Column>();
  closeEvent = output();

  public readonly sort = computed<SortDirection>(
    () => this._context().grid.filters.sortModel()[this.column().getColId()] ?? null
  );
  public readonly hasFilter = computed(() => {
    this._context().grid.filters.changedAt();
    return this.column().isFilterActive();
  });
  public filterEnabled = false;
  public sortable = false;

  private readonly _filterSlot = viewChild<ElementRef<HTMLElement>>('filterSlot');
  private _filter: ColumnFilter | null = null;
  private readonly _context = computed(() => this.api().getGridOption('context') as GridContext);

  ngOnInit(): void {
    const column = this.column();
    this.sortable = column.isSortable();
    this.filterEnabled = !!column.getColDef().filter;
  }

  ngAfterViewInit(): void {
    if (!this.filterEnabled) {
      return;
    }
    this.api()
      .getColumnFilterInstance(this.column())
      .then(filter => this._attachFilter((filter as unknown as ColumnFilter | null | undefined) ?? null));
  }

  ngOnDestroy(): void {
    this._filter?.host.remove();
    this._filter = null;
  }

  public setSort(direction: SortDirection): void {
    const sort = this.sort() === direction ? null : direction;
    this.api().applyColumnState({
      defaultState: { sort: null },
      state: [{ colId: this.column().getColId(), sort }],
    });
    this._persist();
    this.closeEvent.emit();
  }

  public clear(): void {
    this.api()
      .setColumnFilterModel(this.column(), null)
      .then(() => {
        this.api().onFilterChanged();
        this._persist();
        this._filter?.afterGuiAttached();
      });
  }

  private _attachFilter(filter: ColumnFilter | null): void {
    const slot = this._filterSlot()?.nativeElement;
    if (!filter?.host || !slot) {
      return;
    }
    this._filter = filter;
    slot.appendChild(filter.host);
    filter.afterGuiAttached();
  }

  private _persist(): void {
    this._context().grid.filters.applyFilterModel({}, FilterModelSource.storage);
  }
}
